/**
 * IEEE 754 floating-point format encode/decode (format-specific functions).
 *
 * Supports: float32, float16, bfloat16, OFP8 E4M3, OFP8 E5M2.
 * Port of pysim8/src/pysim8/fp_formats.py to JavaScript ES module.
 * No simulator dependencies -- used by both assembler and simulator.
 */

import { FP_FMT_F, FP_FMT_H, FP_FMT_BF, FP_FMT_O3, FP_FMT_O2 } from "./isa.js";

// ── Rounding modes ──────────────────────────────────────────────

export const RoundingMode = Object.freeze({
    RNE: 0, // Round to Nearest, ties to Even
    RTZ: 1, // Round toward Zero
    RDN: 2, // Round toward -Inf
    RUP: 3, // Round toward +Inf
});

// ── Exception flags ─────────────────────────────────────────────

export const NO_EXC = Object.freeze({
    invalid: false,
    divZero: false,
    overflow: false,
    underflow: false,
    inexact: false,
});

export function exc({ invalid = false, divZero = false, overflow = false, underflow = false, inexact = false } = {}) {
    return Object.freeze({ invalid, divZero, overflow, underflow, inexact });
}

export function excReplace(base, overrides) {
    return exc({
        invalid: overrides.invalid ?? base.invalid,
        divZero: overrides.divZero ?? base.divZero,
        overflow: overrides.overflow ?? base.overflow,
        underflow: overrides.underflow ?? base.underflow,
        inexact: overrides.inexact ?? base.inexact,
    });
}

// ── Shared typed-array buffers for float32 <-> bits ─────────────

const _f32buf = new ArrayBuffer(4);
const _f32arr = new Float32Array(_f32buf);
const _u32arr = new Uint32Array(_f32buf);

// ── nextafter / copysign helpers ────────────────────────────────

const _f64buf = new ArrayBuffer(8);
const _f64view = new Float64Array(_f64buf);
const _i64view = new BigInt64Array(_f64buf);

export function nextafter(from, to) {
    if (Number.isNaN(from) || Number.isNaN(to)) return NaN;
    if (from === to) return to;
    if (from === 0) {
        return to > 0 ? 5e-324 : -5e-324;
    }
    _f64view[0] = from;
    if (from < to === from > 0) {
        _i64view[0] += 1n;
    } else {
        _i64view[0] -= 1n;
    }
    return _f64view[0];
}

export function copysign(x, y) {
    const ax = Math.abs(x);
    return y < 0 || Object.is(y, -0) ? -ax : ax;
}

// ── Little-endian byte helpers ──────────────────────────────────

/** Pack a 16-bit integer into a 2-byte little-endian Uint8Array. */
function _u16ToLE(bits) {
    return new Uint8Array([bits & 0xff, (bits >> 8) & 0xff]);
}

/** Pack a 32-bit integer into a 4-byte little-endian Uint8Array. */
function _u32ToLE(bits) {
    return new Uint8Array([bits & 0xff, (bits >> 8) & 0xff, (bits >> 16) & 0xff, (bits >> 24) & 0xff]);
}

/** Extract sign and absolute value, treating -0 as negative. Returns [sign, absVal]. */
function _extractSign(value) {
    if (value < 0 || (value === 0 && Object.is(value, -0))) return [1, -value];
    return [0, value];
}

// ── IEEE 754 boundary constants ─────────────────────────────────

const _MIN_NORMAL_F32 = 1.1754943508222875e-38; // 2^-126
const _MIN_NORMAL_F16 = 6.103515625e-5; // 2^-14
const _OVERFLOW_THRESH_F32 = 3.4028235677973366e38;
const _OVERFLOW_THRESH_F16 = 65520.0;

// ── float32 ─────────────────────────────────────────────────────

export function encodeFloat32(value, rm = 0) {
    if (Number.isNaN(value) || !Number.isFinite(value) || value === 0) {
        // Use hardware float32 for NaN, Inf, zero
        _f32arr[0] = value;
        return { data: _u32ToLE(_u32arr[0]), exc: NO_EXC };
    }

    if (rm !== RoundingMode.RNE) {
        return _encodeIeeeDirected(value, 8, 23, 127, rm);
    }

    // RNE: use hardware Float32Array for accurate rounding
    if (Math.abs(value) >= _OVERFLOW_THRESH_F32) {
        _f32arr[0] = value < 0 ? -Infinity : Infinity;
        return { data: _u32ToLE(_u32arr[0]), exc: exc({ overflow: true, inexact: true }) };
    }

    _f32arr[0] = value;
    const f32Bits = _u32arr[0];
    const rt = _f32arr[0];
    const data = _u32ToLE(f32Bits);

    const overflow = !Number.isFinite(rt) && Number.isFinite(value);
    const expBits = (f32Bits >>> 23) & 0xff;
    const isSub = expBits === 0 && (f32Bits & 0x7fffff) !== 0;
    const flushedToZero = rt === 0 && value !== 0;
    const exactSubnormal = 0 < Math.abs(value) && Math.abs(value) < _MIN_NORMAL_F32;
    const underflow = isSub || flushedToZero || exactSubnormal;
    const inexact = rt !== value;

    return {
        data,
        exc: exc({ overflow, underflow, inexact: inexact || overflow }),
    };
}

export function decodeFloat32(data) {
    _u32arr[0] = data[0] | (data[1] << 8) | (data[2] << 16) | ((data[3] << 24) >>> 0);
    return _f32arr[0];
}

// ── float16 ─────────────────────────────────────────────────────

// Manual float16 encode (no DataView.getFloat16 for portability)
function _encodeFloat16Rne(value) {
    // Get float32 bits
    _f32arr[0] = value;
    const f32Bits = _u32arr[0];

    const f32Sign = (f32Bits >>> 31) & 1;
    const f32Exp = (f32Bits >>> 23) & 0xff;
    const f32Mant = f32Bits & 0x7fffff;

    let f16Sign = f32Sign;
    let f16Exp;
    let f16Mant;

    const unbiasedExp = f32Exp - 127;

    if (f32Exp === 0) {
        // f32 subnormal or zero -> f16 zero (too tiny for f16 subnormals)
        f16Exp = 0;
        f16Mant = 0;
    } else if (f32Exp === 0xff) {
        // Inf or NaN in f32
        f16Exp = 0x1f;
        f16Mant = f32Mant !== 0 ? 0x200 : 0; // preserve NaN vs Inf
    } else if (unbiasedExp < -24) {
        // Too small: rounds to zero
        f16Exp = 0;
        f16Mant = 0;
    } else if (unbiasedExp < -14) {
        // f16 subnormal range
        f16Exp = 0;
        // Full significand with implicit 1: (1.mant) * 2^(unbiasedExp)
        // f16 subnormals: (0.mant) * 2^(-14)
        // So shift amount = -14 - unbiasedExp + 13 (since f32 has 23 mant bits, f16 has 10)
        const fullMant = (1 << 23) | f32Mant;
        // We want to shift from 23-bit mantissa to 10-bit, plus extra shift for subnormal
        // shiftAmount = -1 - unbiasedExp + 13  (= 13 - unbiasedExp - 1)
        // Actually: denorm shift
        // f16 denorm value = mant/1024 * 2^(-14)
        // = fullMant * 2^(unbiasedExp - 23) but we need mant/1024 * 2^(-14)
        // so mant = fullMant * 2^(unbiasedExp + 14 - 23) = fullMant >> (9 - unbiasedExp - 14)
        // = fullMant >> (23 - 10 - (unbiasedExp + 14))
        // = fullMant >> (23 - 10 - unbiasedExp - 14)
        // = fullMant >> (-1 - unbiasedExp)
        const shift = -1 - unbiasedExp; // how many bits to shift right from 23-bit
        // We need to keep 10 bits, but after shifting for subnormality
        // Round to nearest even
        const shifted = fullMant >> shift;
        const remainder = fullMant & ((1 << shift) - 1);
        const halfway = 1 << (shift - 1);
        if (remainder > halfway) {
            f16Mant = shifted + 1;
        } else if (remainder === halfway) {
            // Tie: round to even
            f16Mant = shifted & 1 ? shifted + 1 : shifted;
        } else {
            f16Mant = shifted;
        }
        // If rounding overflows into exp=1, that's okay
        if (f16Mant >= 1 << 10) {
            f16Exp = 1;
            f16Mant = 0;
        } else {
            f16Mant &= 0x3ff;
        }
    } else if (unbiasedExp > 15) {
        // Overflow: Inf
        f16Exp = 0x1f;
        f16Mant = 0;
    } else {
        // Normal f16
        f16Exp = unbiasedExp + 15;
        // Round 23-bit mantissa to 10 bits with RNE
        const dropped = f32Mant & 0x1fff; // lower 13 bits
        const halfway = 0x1000;
        f16Mant = f32Mant >> 13;
        if (dropped > halfway) {
            f16Mant += 1;
        } else if (dropped === halfway) {
            // Tie: round to even
            if (f16Mant & 1) {
                f16Mant += 1;
            }
        }
        // Mantissa overflow -> exponent bump
        if (f16Mant > 0x3ff) {
            f16Mant = 0;
            f16Exp += 1;
            if (f16Exp >= 0x1f) {
                f16Exp = 0x1f;
                f16Mant = 0;
            }
        }
    }

    return (f16Sign << 15) | (f16Exp << 10) | f16Mant;
}

export function encodeFloat16(value, rm = 0) {
    if (Number.isNaN(value)) {
        return { data: _u16ToLE(0x7e00), exc: NO_EXC };
    }
    if (!Number.isFinite(value)) {
        return { data: _u16ToLE(value > 0 ? 0x7c00 : 0xfc00), exc: NO_EXC };
    }
    if (value === 0) {
        return { data: _u16ToLE(Object.is(value, -0) ? 0x8000 : 0x0000), exc: NO_EXC };
    }

    if (rm !== RoundingMode.RNE) {
        return _encodeIeeeDirected(value, 5, 10, 15, rm);
    }

    // RNE
    if (Math.abs(value) >= _OVERFLOW_THRESH_F16) {
        return { data: _u16ToLE(value < 0 ? 0xfc00 : 0x7c00), exc: exc({ overflow: true, inexact: true }) };
    }

    const f16Bits = _encodeFloat16Rne(value);
    const data = _u16ToLE(f16Bits);

    const rt = decodeFloat16Bits(f16Bits);
    const overflow = !Number.isFinite(rt) && Number.isFinite(value);
    const expBits = (f16Bits >> 10) & 0x1f;
    const isSub = expBits === 0 && (f16Bits & 0x3ff) !== 0;
    const flushedToZero = rt === 0 && value !== 0;
    const exactSubnormal = 0 < Math.abs(value) && Math.abs(value) < _MIN_NORMAL_F16;
    const underflow = isSub || flushedToZero || exactSubnormal;
    const inexact = rt !== value;

    return {
        data,
        exc: exc({ overflow, underflow, inexact: inexact || overflow }),
    };
}

export function decodeFloat16Bits(bits) {
    const sign = (bits >>> 15) & 1;
    const expField = (bits >>> 10) & 0x1f;
    const mant = bits & 0x3ff;

    if (expField === 0x1f) {
        if (mant !== 0) return NaN;
        return sign ? -Infinity : Infinity;
    }
    if (expField === 0) {
        if (mant === 0) return sign ? -0.0 : 0.0;
        const val = (mant / 1024) * Math.pow(2, -14);
        return sign ? -val : val;
    }
    const val = (1.0 + mant / 1024) * Math.pow(2, expField - 15);
    return sign ? -val : val;
}

export function decodeFloat16(data) {
    const bits = data[0] | (data[1] << 8);
    return decodeFloat16Bits(bits);
}

// ── bfloat16 ────────────────────────────────────────────────────

function _roundF32ToBf16(f32Bits) {
    let upper = (f32Bits >>> 16) & 0xffff;
    const lower = f32Bits & 0xffff;

    const halfway = 0x8000;
    if (lower > halfway) {
        upper += 1;
    } else if (lower === halfway) {
        if (upper & 1) {
            upper += 1;
        }
    }

    return upper;
}

export function encodeBfloat16(value, rm = 0) {
    if (Number.isNaN(value)) {
        return { data: _u16ToLE(0x7fc0), exc: NO_EXC };
    }
    if (!Number.isFinite(value)) {
        return { data: _u16ToLE(value > 0 ? 0x7f80 : 0xff80), exc: NO_EXC };
    }
    if (value === 0) {
        // Preserve sign of zero: get from float32
        _f32arr[0] = value;
        return { data: _u16ToLE((_u32arr[0] >>> 16) & 0xffff), exc: NO_EXC };
    }

    if (rm !== RoundingMode.RNE) {
        return _encodeIeeeDirected(value, 8, 7, 127, rm);
    }

    // RNE: pack to float32, then round upper 16 bits
    if (Math.abs(value) >= _OVERFLOW_THRESH_F32) {
        return { data: _u16ToLE(value < 0 ? 0xff80 : 0x7f80), exc: exc({ overflow: true, inexact: true }) };
    }

    _f32arr[0] = value;
    const f32Bits = _u32arr[0];
    const lower = f32Bits & 0xffff;

    const upper = _roundF32ToBf16(f32Bits);
    const data = _u16ToLE(upper);

    // Check if rounding caused overflow to Inf
    const rt = decodeBfloat16(data);
    const overflow = !Number.isFinite(rt) && Number.isFinite(value);
    const inexact = lower !== 0;
    // Underflow: result is subnormal bf16 or flushed to zero
    const bfExp = (upper >> 7) & 0xff;
    const bfMant = upper & 0x7f;
    const flushed = rt === 0 && value !== 0;
    const underflow = (bfExp === 0 && bfMant !== 0) || flushed;

    return {
        data,
        exc: exc({ overflow, underflow, inexact: inexact || overflow }),
    };
}

export function decodeBfloat16(data) {
    // bfloat16 is the upper 16 bits of float32 in LE
    // So bfloat16 LE bytes become bytes [2:3] of float32 LE
    const upper = data[0] | (data[1] << 8);
    _u32arr[0] = upper << 16;
    return _f32arr[0];
}

// ── OFP8 E4M3 ──────────────────────────────────────────────────

const _E4M3_BIAS = 7;
const _E4M3_EXP_BITS = 4;
const _E4M3_MANT_BITS = 3;
const _E4M3_MAX_EXP = (1 << _E4M3_EXP_BITS) - 1; // 15
const _E4M3_MAX_FINITE = 448.0;

export function encodeOfp8E4M3(value, rm = 0) {
    if (Number.isNaN(value)) {
        return { data: new Uint8Array([0x7f]), exc: exc({ invalid: true }) };
    }

    const [sign, absVal] = _extractSign(value);

    if (!Number.isFinite(absVal)) {
        // No Inf in E4M3 -- saturate to max finite
        return { data: new Uint8Array([(sign << 7) | 0x7e]), exc: exc({ overflow: true, inexact: true }) };
    }
    if (absVal === 0) {
        return { data: new Uint8Array([sign << 7]), exc: NO_EXC };
    }
    if (absVal > _E4M3_MAX_FINITE) {
        return { data: new Uint8Array([(sign << 7) | 0x7e]), exc: exc({ overflow: true, inexact: true }) };
    }

    const { byteVal, exc: mExc } = _encodeMiniFloat(
        absVal,
        sign,
        _E4M3_EXP_BITS,
        _E4M3_MANT_BITS,
        _E4M3_BIAS,
        false,
        0x7f,
        rm,
    );
    return { data: new Uint8Array([byteVal]), exc: mExc };
}

export function decodeOfp8E4M3(byteVal) {
    if (typeof byteVal !== "number") byteVal = byteVal[0];
    const sign = (byteVal >> 7) & 1;
    const expField = (byteVal >> _E4M3_MANT_BITS) & _E4M3_MAX_EXP;
    const mant = byteVal & ((1 << _E4M3_MANT_BITS) - 1);

    if (expField === _E4M3_MAX_EXP && mant === (1 << _E4M3_MANT_BITS) - 1) {
        return NaN;
    }

    if (expField === 0) {
        if (mant === 0) return sign ? -0.0 : 0.0;
        const val = (mant / (1 << _E4M3_MANT_BITS)) * Math.pow(2, 1 - _E4M3_BIAS);
        return sign ? -val : val;
    }

    const val = (1.0 + mant / (1 << _E4M3_MANT_BITS)) * Math.pow(2, expField - _E4M3_BIAS);
    return sign ? -val : val;
}

// ── OFP8 E5M2 ──────────────────────────────────────────────────

const _E5M2_BIAS = 15;
const _E5M2_EXP_BITS = 5;
const _E5M2_MANT_BITS = 2;
const _E5M2_MAX_EXP = (1 << _E5M2_EXP_BITS) - 1; // 31

export function encodeOfp8E5M2(value, rm = 0) {
    if (Number.isNaN(value)) {
        // Canonical NaN: exp=31, mant=1 (quiet NaN) = 0x7D
        return { data: new Uint8Array([0x7d]), exc: exc({ invalid: true }) };
    }

    const [sign, absVal] = _extractSign(value);

    if (!Number.isFinite(absVal)) {
        return { data: new Uint8Array([(sign << 7) | (_E5M2_MAX_EXP << _E5M2_MANT_BITS)]), exc: NO_EXC };
    }
    if (absVal === 0) {
        return { data: new Uint8Array([sign << 7]), exc: NO_EXC };
    }

    const { byteVal, exc: mExc } = _encodeMiniFloat(
        absVal,
        sign,
        _E5M2_EXP_BITS,
        _E5M2_MANT_BITS,
        _E5M2_BIAS,
        true,
        null,
        rm,
    );
    return { data: new Uint8Array([byteVal]), exc: mExc };
}

export function decodeOfp8E5M2(byteVal) {
    if (typeof byteVal !== "number") byteVal = byteVal[0];
    const sign = (byteVal >> 7) & 1;
    const expField = (byteVal >> _E5M2_MANT_BITS) & _E5M2_MAX_EXP;
    const mant = byteVal & ((1 << _E5M2_MANT_BITS) - 1);

    if (expField === _E5M2_MAX_EXP) {
        if (mant === 0) return sign ? -Infinity : Infinity;
        return NaN;
    }

    if (expField === 0) {
        if (mant === 0) return sign ? -0.0 : 0.0;
        const val = (mant / (1 << _E5M2_MANT_BITS)) * Math.pow(2, 1 - _E5M2_BIAS);
        return sign ? -val : val;
    }

    const val = (1.0 + mant / (1 << _E5M2_MANT_BITS)) * Math.pow(2, expField - _E5M2_BIAS);
    return sign ? -val : val;
}

// ── Generic mini-float encoder ──────────────────────────────────

function _overflowReturnsInf(rm, sign) {
    if (rm === RoundingMode.RTZ) return false;
    if (rm === RoundingMode.RDN) return sign === 1;
    if (rm === RoundingMode.RUP) return sign === 0;
    return true; // RNE
}

function _overflowResult(sign, expBits, mantBits, maxExp, maxNormalBiased, hasInf, nanPattern, rm) {
    if (hasInf && _overflowReturnsInf(rm, sign)) {
        const byteVal = (sign << (expBits + mantBits)) | (maxExp << mantBits);
        return { byteVal, exc: exc({ overflow: true, inexact: true }) };
    }
    let maxMant = (1 << mantBits) - 1;
    if (nanPattern !== null) {
        maxMant -= 1;
    }
    const byteVal = (sign << (expBits + mantBits)) | (maxNormalBiased << mantBits) | maxMant;
    return { byteVal, exc: exc({ overflow: true, inexact: true }) };
}

function _encodeMiniFloat(absVal, sign, expBits, mantBits, bias, hasInf, nanPattern, rm) {
    const maxExp = (1 << expBits) - 1;
    const maxNormalBiased = hasInf ? maxExp - 1 : maxExp;

    // Compute unbiased exponent (absVal > 0 guaranteed by callers)
    const log2Val = Math.floor(Math.log2(absVal));
    let biasedExp = log2Val + bias;
    let mantInt;
    let underflow;

    if (biasedExp <= 0) {
        // Denormalized number
        biasedExp = 0;
        const scale = Math.pow(2, 1 - bias);
        const mantFrac = absVal / scale;
        mantInt = _roundMantissa(mantFrac, mantBits, rm, sign);
        // Check if rounding promoted to normal
        if (mantInt >= 1 << mantBits) {
            biasedExp = 1;
            mantInt -= 1 << mantBits;
        }
        underflow = true;
    } else if (biasedExp > maxNormalBiased) {
        return _overflowResult(sign, expBits, mantBits, maxExp, maxNormalBiased, hasInf, nanPattern, rm);
    } else {
        // Normal number
        const significand = absVal / Math.pow(2, log2Val); // 1.xxx
        const mantFrac = Math.max(0.0, significand - 1.0);
        mantInt = _roundMantissa(mantFrac, mantBits, rm, sign);
        // Rounding may cause mantissa overflow
        if (mantInt >= 1 << mantBits) {
            mantInt = 0;
            biasedExp += 1;
            if (biasedExp > maxNormalBiased) {
                return _overflowResult(sign, expBits, mantBits, maxExp, maxNormalBiased, hasInf, nanPattern, rm);
            }
        }
        // Check for E4M3 NaN collision
        if (!hasInf && nanPattern !== null) {
            const candidate = (biasedExp << mantBits) | mantInt;
            if (candidate === (nanPattern & ((1 << (expBits + mantBits)) - 1))) {
                mantInt -= 1;
            }
        }
        underflow = false;
    }

    const byteVal = (sign << (expBits + mantBits)) | (biasedExp << mantBits) | mantInt;

    // Recompute inexact by round-tripping
    let rtVal;
    if (biasedExp === 0) {
        rtVal = (mantInt / (1 << mantBits)) * Math.pow(2, 1 - bias);
    } else {
        rtVal = (1.0 + mantInt / (1 << mantBits)) * Math.pow(2, biasedExp - bias);
    }
    const inexact = rtVal !== absVal;

    return { byteVal, exc: exc({ underflow, inexact }) };
}

function _encodeIeeeDirected(value, expBits, mantBits, bias, rm) {
    let sign = 0;
    let absVal = value;
    if (value < 0) {
        sign = 1;
        absVal = -value;
    }
    const { byteVal: bits, exc: mExc } = _encodeMiniFloat(absVal, sign, expBits, mantBits, bias, true, null, rm);
    const width = 1 + expBits + mantBits;
    const numBytes = width >> 3;
    const data = Uint8Array.from({ length: numBytes }, (_, i) => (bits >> (i * 8)) & 0xff);
    return { data, exc: mExc };
}

function _roundMantissa(frac, mantBits, rm, sign) {
    const scale = 1 << mantBits;
    const scaled = frac * scale;
    const floorVal = Math.floor(scaled);
    const remainder = scaled - floorVal;

    if (remainder === 0.0) return floorVal;

    if (rm === RoundingMode.RNE) {
        if (remainder > 0.5) return floorVal + 1;
        if (remainder < 0.5) return floorVal;
        // Tie: round to even
        return floorVal & 1 ? floorVal + 1 : floorVal;
    }
    if (rm === RoundingMode.RTZ) {
        return floorVal;
    }
    if (rm === RoundingMode.RDN) {
        // Toward -Inf: if negative, round away from zero (up magnitude)
        return sign ? floorVal + 1 : floorVal;
    }
    // RUP: toward +Inf: if positive, round away from zero
    return sign ? floorVal : floorVal + 1;
}

// ── Dispatcher functions ────────────────────────────────────────

export function floatToBytes(value, fmt, rm = 0) {
    if (fmt === FP_FMT_F) return encodeFloat32(value, rm);
    if (fmt === FP_FMT_H) return encodeFloat16(value, rm);
    if (fmt === FP_FMT_BF) return encodeBfloat16(value, rm);
    if (fmt === FP_FMT_O3) return encodeOfp8E4M3(value, rm);
    if (fmt === FP_FMT_O2) return encodeOfp8E5M2(value, rm);
    throw new Error(`unsupported FP format code: ${fmt}`);
}

export function bytesToFloat(data, fmt) {
    if (fmt === FP_FMT_F) return decodeFloat32(data);
    if (fmt === FP_FMT_H) return decodeFloat16(data);
    if (fmt === FP_FMT_BF) return decodeBfloat16(data);
    if (fmt === FP_FMT_O3) return decodeOfp8E4M3(data[0]);
    if (fmt === FP_FMT_O2) return decodeOfp8E5M2(data[0]);
    throw new Error(`unsupported FP format code: ${fmt}`);
}
